import React, { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import HeaderV2 from '../components/HeaderV2'
import XinyiHeroV2 from './store/xinyi/v2/XinyiHeroV2'
import XinyiAboutSplitV2 from './store/xinyi/v2/XinyiAboutSplitV2'
import XinyiInfoSplitV2 from './store/xinyi/v2/XinyiInfoSplitV2'
import XinyiHowToGetHereV2 from './store/xinyi/v2/XinyiHowToGetHereV2'
import XinyiGuideV2 from './store/xinyi/v2/XinyiGuideV2'
import XinyiResearchV2 from './store/xinyi/v2/XinyiResearchV2'
import XinyiFAQV2 from './store/xinyi/v2/XinyiFAQV2'
import { xinyiFAQ } from './store/xinyi/v2/content'

/**
 * XinyiStoreV2Page — /{lang}/store/xinyi/v2
 * Barista Map style Xinyi store page. Old /{lang}/store/xinyi remains
 * untouched (per "preserve all originals, V2 lives in parallel" rule).
 *
 * Sections (top → bottom):
 *  1. HeaderV2  (solid white, default variant)
 *  2. h-20 spacer
 *  3. XinyiHeroV2          full-bleed image + bottom big serif title
 *  4. XinyiAboutSplitV2    Brand narrative + studio image
 *  5. XinyiInfoSplitV2     Hours / address / phone / IG + space photo
 *  6. XinyiHowToGetHereV2  Expandable directions + Google Maps
 *  7. XinyiGuideV2         Expandable Quick Guide + 3 CTAs
 *  8. XinyiResearchV2      3 alternating splits (Research / Quality / R&B)
 *  9. XinyiFAQV2           Minimal FAQ + Schema.org
 *
 * FooterV2 is rendered at the App router level.
 */
const XinyiStoreV2Page: React.FC = () => {
  const location = useLocation()
  const currentLang: 'zh' | 'en' = location.pathname.startsWith('/en') ? 'en' : 'zh'
  const isPreviewRoute = location.pathname.includes('/v2')

  // Inject baristamap-style fonts (EB Garamond + Noto Serif TC) once per mount
  useEffect(() => {
    const ID = 'ongu-v2-fonts'
    if (document.getElementById(ID)) return
    const link = document.createElement('link')
    link.id = ID
    link.rel = 'stylesheet'
    link.href =
      'https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Noto+Serif+TC:wght@300;400;500&display=swap'
    document.head.appendChild(link)
  }, [])

  const seo = useMemo(
    () =>
      currentLang === 'zh'
        ? {
            title: '信義｜ONGU Studio - 信義 (V2 預覽)｜自家烘焙・職人手沖咖啡廳',
            description:
              'ONGU Studio 信義店：信義商圈步行 5–8 分鐘，自家烘焙、季節手沖、不鏽鋼工業吧台，專注於乾淨、平衡、層次清晰的咖啡風味。',
          }
        : {
            title: 'Xinyi｜ONGU Studio – Xinyi (V2 Preview)｜House-roasted Coffee & Pour-overs',
            description:
              'ONGU Studio Xinyi: a 5–8 min walk from the Xinyi shopping district. House roasting, seasonal pour-overs, a stainless-steel industrial bar — clean, balanced, layered specialty coffee.',
          },
    [currentLang]
  )

  // FAQ Schema.org JSON-LD — same shape as original XinyiStorePage
  const faqSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: xinyiFAQ.map((item) => ({
        '@type': 'Question',
        name: item.q[currentLang],
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a[currentLang],
        },
      })),
    }),
    [currentLang]
  )

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        {isPreviewRoute && <meta name="robots" content="noindex,nofollow" />}
        {isPreviewRoute && (
          <link rel="canonical" href={`https://www.ongucoffee.com/${currentLang}/store/xinyi`} />
        )}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div
        className="bg-[#FAF9F1] text-black"
        style={{ fontFamily: "'Noto Sans TC', 'Helvetica Neue', sans-serif" }}
      >
        {/* Solid white header (default variant) */}
        <HeaderV2 />

        {/* Spacer for fixed navbar height */}
        <div className="h-20" />

        {/* 1. Hero — full-bleed image + big serif title */}
        <XinyiHeroV2 currentLang={currentLang} />

        {/* 2. Brand narrative */}
        <XinyiAboutSplitV2 currentLang={currentLang} />

        {/* 3. Visit info + space photo */}
        <XinyiInfoSplitV2 currentLang={currentLang} />

        {/* 4. How to get here (expandable) */}
        <XinyiHowToGetHereV2 currentLang={currentLang} />

        {/* 5. Quick guide (expandable) */}
        <XinyiGuideV2 currentLang={currentLang} />

        {/* 6. Research / Quality / Roasting & Brewing */}
        <XinyiResearchV2 currentLang={currentLang} />

        {/* 7. FAQ */}
        <XinyiFAQV2 currentLang={currentLang} />

        {/* Bottom spacer before FooterV2 (rendered by App.tsx) */}
        <div className="h-12 md:h-20 bg-[#FAF9F1]" />
      </div>
    </>
  )
}

export default XinyiStoreV2Page
